import { useCallback, useState } from 'react';

import {
  buscarRadarRelacionamento,
  RadarRelacionamentoResponse,
} from '../../../../api/contac/contacRecomendacoesApi';
import { notify } from '../../../../shared/feedback/notify';

/**
 * O estado do Radar do Relacionamento (contac/at-13).
 *
 * ⚠️ Vive na PÁGINA, não dentro do painel. A tela de placas faz `if (loading) return
 * <FullScreenLoader/>` durante cada carga da lista: um componente filho seria desmontado a cada
 * filtro aplicado e refaria a chamada — que varre 1,8 milhão de boletos. Com o estado aqui, o
 * painel é montado e desmontado à vontade sem custar consulta nenhuma.
 *
 * ## Só os CARTÕES moram aqui (transversal/at-82)
 *
 * Os achados do assistente saíram deste hook. Eles deixaram de depender do período do filtro
 * (análise §3.3: janela fixa, um conjunto por dia para todos), então não têm mais nada a
 * compartilhar com esta carga — quem os busca é a seção compartilhada
 * (`components/radar/useAchadosDoAssistente`), que se vira sozinha. A separação em duas cargas
 * independentes continua valendo pelo mesmo motivo de sempre: a exploração por IA leva segundos e
 * pode falhar, e a fila de trabalho não pode esperar por ela nem herdar o erro dela.
 */
export interface EstadoDoRadarRelacionamento {
  dados: RadarRelacionamentoResponse | null;
  carregando: boolean;
  erro: boolean;
}

export function useRadarRelacionamento() {
  const [dados, setDados] = useState<RadarRelacionamentoResponse | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [jaCarregou, setJaCarregou] = useState(false);

  /** O período usado na última carga — é ele que o detalhe do CARTÃO precisa repetir. */
  const [periodoCarregado, setPeriodoCarregado] = useState<string | undefined>(undefined);

  const carregar = useCallback(async (dataInicio?: string) => {
    setCarregando(true);
    setJaCarregou(true);
    setPeriodoCarregado(dataInicio);
    try {
      setDados(await buscarRadarRelacionamento(dataInicio));
      setErro(false);
    } catch (error) {
      // Estado seguro E sinal (at-60). Nesta tela a confusão é cara em dobro: "nenhum
      // inadimplente" é um desfecho possível e desejável, então falha silenciosa mandaria a
      // equipe de cobrança embora achando que não há o que fazer.
      setDados(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar o Radar do Relacionamento. A lista de placas não é afetada.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  return { dados, carregando, erro, jaCarregou, carregar, periodoCarregado };
}
